import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppLayout from '../components/AppLayout';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useSocketEvent } from '../context/SocketContext';
import ConfirmDialog from '../components/ConfirmDialog';
import DatePicker from '../components/DatePicker';
import ViewModeSwitcher, { useViewMode } from '../components/ViewModeSwitcher';
import { todayISO } from '../utils/clinicDate';
import {
  Pill, Plus, Save, X, Search, PackageOpen, PackageCheck,
  Calendar, ArrowDown, ArrowUp, Loader, Pencil, Trash2,
  IndianRupee, Layers,
} from 'lucide-react';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100];
const CATEGORIES = ['oral-care','whitening','equipment','supplement','anesthesia','instrument','other'];
const UNITS = ['pcs','bottle','box','strip','tube','pair','set','ml','gm'];

// todayISO imported from utils/clinicDate above.
function fmt(n) { return (n||0).toLocaleString('en-IN',{maximumFractionDigits:0}); }

// ── Item picker with autocomplete ────────────────────────────────────────────
function ItemPicker({ value, onSelect, placeholder }) {
  const [q, setQ]           = useState('');
  const [items, setItems]   = useState([]);
  const [open, setOpen]     = useState(false);
  const [loading, setLoading] = useState(false);
  const dRef = useRef(null); const iRef = useRef(null); const dbRef = useRef(null);

  const search = useCallback(async (text) => {
    setLoading(true);
    try {
      const res = await api.get('/pharmacy/items', { params: { search: text } });
      setItems(res.data.items || []);
      setOpen(true);
    } catch { setItems([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    clearTimeout(dbRef.current);
    if (q.length >= 1) { dbRef.current = setTimeout(() => search(q), 250); }
    else if (q.length === 0 && open) { search(''); }
  }, [q, open, search]);

  useEffect(() => {
    function h(e) {
      if (dRef.current && !dRef.current.contains(e.target) && iRef.current && !iRef.current.contains(e.target))
        setOpen(false);
    }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div style={{ position: 'relative' }}>
      {value ? (
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px', background:'var(--color-primary-soft)', borderRadius:8, border:'1px solid var(--color-primary)' }}>
          <Pill size={14} color="var(--color-primary)" />
          <span style={{ fontWeight:700, color:'var(--color-primary)', flex:1 }}>{value.name}</span>
          <span style={{ fontSize:12, color:'var(--color-primary)', opacity:0.7 }}>{value.category} · {value.unit}</span>
          <button type="button" className="btn btn-icon btn-ghost" onClick={() => onSelect(null)}><X size={13}/></button>
        </div>
      ) : (
        <div ref={iRef}>
          <div style={{ position:'relative' }}>
            <Search size={14} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--color-ink-faint)', pointerEvents:'none' }}/>
            <input className="input" style={{ paddingLeft:30 }} value={q}
              onChange={e => setQ(e.target.value)}
              onFocus={() => search(q)}
              placeholder={placeholder || 'Search item…'} autoComplete="off"/>
            {loading && <Loader size={13} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', color:'var(--color-ink-faint)', animation:'spin 1s linear infinite' }}/>}
          </div>
        </div>
      )}
      {open && !value && (
        <div ref={dRef} className="card" style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, zIndex:300, padding:0, maxHeight:220, overflowY:'auto', boxShadow:'var(--shadow-lg)' }}>
          {items.length === 0
            ? <div style={{ padding:'12px 14px', fontSize:13, color:'var(--color-ink-faint)' }}>No items found — you can create one below.</div>
            : items.map(it => (
              <button key={it._id} type="button"
                style={{ width:'100%', textAlign:'left', padding:'10px 14px', border:'none', background:'transparent', cursor:'pointer', borderBottom:'1px solid var(--color-border)', display:'flex', gap:10, alignItems:'center' }}
                onMouseEnter={e => e.currentTarget.style.background='var(--color-primary-soft)'}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}
                onClick={() => { onSelect(it); setQ(''); setOpen(false); }}>
                <Pill size={14} color="var(--color-primary)"/>
                <div>
                  <div style={{ fontWeight:700, fontSize:13 }}>{it.name}</div>
                  <div style={{ fontSize:11, color:'var(--color-ink-faint)' }}>{it.category} · {it.unit} · Supplier ₹{fmt(it.supplierCost)} · Selling ₹{fmt(it.sellingPrice)}</div>
                </div>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

// ── Patient picker (reuses existing search endpoint) ─────────────────────────
function PatientPicker({ value, onSelect }) {
  const [q, setQ] = useState(''); const [results, setResults] = useState([]); const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false); const dbRef = useRef(null); const dRef = useRef(null); const iRef = useRef(null);

  useEffect(() => {
    clearTimeout(dbRef.current);
    if (q.trim().length >= 2) {
      dbRef.current = setTimeout(async () => {
        setSearching(true);
        try { const r = await api.get('/patients/search', { params:{ q: q.trim() } }); setResults(r.data.patients||[]); setOpen(true); }
        catch { setResults([]); } finally { setSearching(false); }
      }, 280);
    } else { setResults([]); }
  }, [q]);

  useEffect(() => {
    function h(e) { if (dRef.current && !dRef.current.contains(e.target) && iRef.current && !iRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div style={{ position:'relative' }}>
      {value ? (
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px', background:'var(--color-accent-soft)', borderRadius:8, border:'1px solid var(--color-accent)' }}>
          <span style={{ fontWeight:700, color:'var(--color-accent)', flex:1 }}>{value.name}</span>
          <span style={{ fontSize:12, color:'var(--color-accent)', opacity:0.8, fontFamily:'var(--font-mono)' }}>{value.patientUID}</span>
          <button type="button" className="btn btn-icon btn-ghost" onClick={() => onSelect(null)}><X size={13}/></button>
        </div>
      ) : (
        <div ref={iRef}>
          <div style={{ position:'relative' }}>
            <Search size={14} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--color-ink-faint)', pointerEvents:'none' }}/>
            <input className="input" style={{ paddingLeft:30 }} value={q} onChange={e => setQ(e.target.value)} placeholder="Search patient…" autoComplete="off"/>
            {searching && <Loader size={13} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', color:'var(--color-ink-faint)', animation:'spin 1s linear infinite' }}/>}
          </div>
        </div>
      )}
      {open && !value && results.length > 0 && (
        <div ref={dRef} className="card" style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, zIndex:300, padding:0, maxHeight:200, overflowY:'auto', boxShadow:'var(--shadow-lg)' }}>
          {results.map(p => (
            <button key={p._id} type="button"
              style={{ width:'100%', textAlign:'left', padding:'10px 14px', border:'none', background:'transparent', cursor:'pointer', borderBottom:'1px solid var(--color-border)' }}
              onMouseEnter={e => e.currentTarget.style.background='var(--color-accent-soft)'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}
              onClick={() => { onSelect(p); setQ(''); setOpen(false); }}>
              <div style={{ fontWeight:700, fontSize:13 }}>{p.name}</div>
              <div style={{ fontSize:11, color:'var(--color-ink-faint)', fontFamily:'var(--font-mono)' }}>{p.patientUID}{p.phone && ` · ${p.phone}`}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function PharmacyPage() {
  const { showToast } = useToast();
  const { user } = useAuth();
  const [txnListMode, setTxnListMode] = useViewMode('pharmacy_txn_view', 'list');
  const [listMode, setListMode] = useViewMode('pharmacy_view', 'list');
  // Remember the last selected date until the user manually changes it —
  // was resetting to "today"/"all" on every visit/reload before.
  const PHARMACY_DATE_KEY = 'pharmacy_dateFilter';
  const todayStr0 = todayISO();
  const [dateFilter, setDateFilterRaw] = useState(() => localStorage.getItem(PHARMACY_DATE_KEY) || 'all');
  function setDateFilter(d) {
    setDateFilterRaw(d);
    localStorage.setItem(PHARMACY_DATE_KEY, d);
  }
  const [calDate, setCalDate] = useState(() => localStorage.getItem(PHARMACY_DATE_KEY) || todayStr0);
  const [calDates, setCalDates] = useState(() => {
    const saved = localStorage.getItem(PHARMACY_DATE_KEY);
    return new Set(saved === 'all' ? [] : [saved || todayStr0]);
  });
  const [activeTab, setActiveTab] = useState('incoming'); // 'incoming' | 'outgoing' | 'catalog'

  const [transactions, setTransactions] = useState([]);
  const [totals, setTotals] = useState({ incomingQty:0, incomingCost:0, outgoingQty:0, outgoingRevenue:0 });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const [catalogItems, setCatalogItems] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  // New transaction form
  const [showForm, setShowForm] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [txnForm, setTxnForm] = useState({ quantity:1, date:todayISO(), supplierCost:'', sellingPrice:'', supplier:'', notes:'' });
  // Multi-item cart for outgoing/sales
  const [outgoingCart, setOutgoingCart] = useState([]); // [{item, qty, sellingPrice}]
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem('pharmacy_groupby') || 'item');
  const [showStats, setShowStats] = useState(() => localStorage.getItem('pharmacy_showStats') !== 'false');
  const toggleStats = () => setShowStats(v => { localStorage.setItem('pharmacy_showStats', !v); return !v; });

  function addToCart(item) {
    setOutgoingCart(prev => {
      const exists = prev.find(c => c.item._id === item._id);
      if (exists) return prev.map(c => c.item._id === item._id ? {...c, qty: c.qty+1} : c);
      return [...prev, { item, qty:1, sellingPrice: item.sellingPrice || 0 }];
    });
  }
  function removeFromCart(itemId) {
    setOutgoingCart(prev => prev.filter(c => c.item._id !== itemId));
  }
  function updateCartQty(itemId, qty) {
    const q = Math.max(1, Number(qty)||1);
    setOutgoingCart(prev => prev.map(c => c.item._id === itemId ? {...c, qty:q} : c));
  }
  function updateCartPrice(itemId, price) {
    setOutgoingCart(prev => prev.map(c => c.item._id === itemId ? {...c, sellingPrice: Number(price)||0} : c));
  }
  const cartTotal = outgoingCart.reduce((s,c) => s + c.qty*(c.sellingPrice||0), 0);
  const [saving, setSaving] = useState(false);

  // New item creation
  const [showItemForm, setShowItemForm] = useState(false);
  const [itemForm, setItemForm] = useState({ name:'', category:'oral-care', unit:'pcs', supplierCost:'', sellingPrice:'', supplier:'', description:'' });
  const [itemSaving, setItemSaving] = useState(false);

  // Edit / delete
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Edit item
  const [editingItemId, setEditingItemId] = useState(null);
  const [editItemForm, setEditItemForm] = useState({});
  const [itemEditSaving, setItemEditSaving] = useState(false);
  const [deleteItemTarget, setDeleteItemTarget] = useState(null);
  const [deletingItem, setDeletingItem] = useState(false);

  // ── Load ──────────────────────────────────────────────────────────────────
  const loadTransactions = useCallback(async () => {
    setLoading(true);
    try {
      // Backend combines date + search independently, so a selected date
      // and an active search term narrow together instead of the date
      // being silently ignored while search text is present.
      const res = await api.get('/pharmacy/transactions', { params:{ type: activeTab === 'catalog' ? 'all' : activeTab, date: dateFilter, search: debouncedSearch, page, limit } });
      setTransactions(res.data.transactions);
      setTotals(res.data.totals);
      setTotal(res.data.total);
    } catch { showToast('Could not load pharmacy records.', 'error'); }
    finally { setLoading(false); }
  }, [activeTab, dateFilter, debouncedSearch, page, limit, showToast]);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try { const res = await api.get('/pharmacy/items', { params:{ includeInactive:'true' } }); setCatalogItems(res.data.items||[]); }
    catch { showToast('Could not load catalog.', 'error'); }
    finally { setCatalogLoading(false); }
  }, [showToast]);

  useEffect(() => {
    if (activeTab === 'catalog') loadCatalog();
    else { setPage(1); loadTransactions(); }
  }, [activeTab, dateFilter, limit, debouncedSearch]); // eslint-disable-line

  useEffect(() => { if (activeTab !== 'catalog') loadTransactions(); }, [page]); // eslint-disable-line

  useSocketEvent('pharmacy:transaction-created', () => { if (activeTab !== 'catalog') loadTransactions(); });
  useSocketEvent('pharmacy:item-updated', () => { if (activeTab === 'catalog') loadCatalog(); });
  // Low-stock notifications
  const [lowStockNotifs, setLowStockNotifs] = React.useState([]);
  useSocketEvent('pharmacy:low-stock', (data) => {
    setLowStockNotifs(prev => {
      // Deduplicate by itemId
      const exists = prev.some(n => String(n.itemId) === String(data.itemId));
      return exists ? prev.map(n => String(n.itemId) === String(data.itemId) ? data : n) : [...prev, data];
    });
  });

  // When item is selected, auto-fill prices
  useEffect(() => {
    if (selectedItem) {
      setTxnForm(f => ({
        ...f,
        supplierCost: selectedItem.supplierCost || '',
        sellingPrice: selectedItem.sellingPrice || '',
        supplier: selectedItem.supplier || f.supplier,
      }));
    }
  }, [selectedItem]);

  // ── Submit transaction ─────────────────────────────────────────────────────
  async function handleSaveTxn(e) {
    e.preventDefault();

    if (activeTab === 'outgoing') {
      if (outgoingCart.length === 0) { showToast('Add at least one item to the cart.', 'error'); return; }
      if (!selectedPatient) { showToast('Please select the patient this sale is for.', 'error'); return; }
      setSaving(true);
      try {
        for (const cartItem of outgoingCart) {
          await api.post('/pharmacy/transactions', {
            transactionType: 'outgoing',
            itemId:       cartItem.item._id,
            quantity:     cartItem.qty,
            date:         txnForm.date,
            sellingPrice: cartItem.sellingPrice,
            patientId:    selectedPatient._id  || '',
            patientName:  selectedPatient.name || '',
            patientUID:   selectedPatient.patientUID || '',
            notes:        txnForm.notes,
          });
        }
        showToast(`${outgoingCart.length} item(s) sold — ${outgoingCart.map(c=>`${c.qty}× ${c.item.name}`).join(', ')}`, 'success');
        setOutgoingCart([]);
        setSelectedPatient(null);
        setTxnForm({ quantity:1, date:todayISO(), supplierCost:'', sellingPrice:'', supplier:'', notes:'' });
        setShowForm(false);
        loadTransactions();
      } catch (err) {
        showToast('Sale failed: ' + (err.response?.data?.message || err.message), 'error');
      } finally {
        setSaving(false);
      }
      return;
    }

    // Incoming — single item
    if (!selectedItem) { showToast('Please select an item.', 'error'); return; }
    setSaving(true);
    try {
      await api.post('/pharmacy/transactions', {
        transactionType: activeTab,
        itemId: selectedItem._id,
        quantity: Number(txnForm.quantity),
        date: txnForm.date,
        supplierCost: Number(txnForm.supplierCost) || 0,
        sellingPrice: Number(txnForm.sellingPrice) || 0,
        patientId:    selectedPatient?._id || null,
        patientName:  selectedPatient?.name || '',
        patientUID:   selectedPatient?.patientUID || '',
        supplier: txnForm.supplier,
        notes: txnForm.notes,
      });
      const label = activeTab === 'incoming' ? 'Stock received' : 'Sale recorded';
      showToast(`${label} — ${txnForm.quantity}× ${selectedItem.name}`, 'success');
      setSelectedItem(null); setSelectedPatient(null);
      setTxnForm({ quantity:1, date:todayISO(), supplierCost:'', sellingPrice:'', supplier:'', notes:'' });
      setShowForm(false);
      loadTransactions();
    } catch (err) { showToast(err.response?.data?.message || 'Could not save.', 'error'); }
    finally { setSaving(false); }
  }

  // ── Create catalog item ────────────────────────────────────────────────────
  async function handleCreateItem(e) {
    e.preventDefault();
    if (!itemForm.name.trim()) { showToast('Item name is required.', 'error'); return; }
    setItemSaving(true);
    try {
      const res = await api.post('/pharmacy/items', { ...itemForm, supplierCost: Number(itemForm.supplierCost)||0, sellingPrice: Number(itemForm.sellingPrice)||0 });
      showToast(`"${res.data.item.name}" added to catalog.`, 'success');
      setItemForm({ name:'', category:'oral-care', unit:'pcs', supplierCost:'', sellingPrice:'', supplier:'', description:'' });
      setShowItemForm(false);
      loadCatalog();
      // If we're on incoming/outgoing, refresh item picker too
    } catch (err) { showToast(err.response?.data?.message || 'Could not create item.', 'error'); }
    finally { setItemSaving(false); }
  }

  // ── Edit item ──────────────────────────────────────────────────────────────
  async function handleSaveItemEdit(id) {
    setItemEditSaving(true);
    try {
      const res = await api.put(`/pharmacy/items/${id}`, { ...editItemForm, supplierCost: Number(editItemForm.supplierCost)||0, sellingPrice: Number(editItemForm.sellingPrice)||0 });
      showToast('Item updated.', 'success');
      setCatalogItems(prev => prev.map(i => i._id === id ? res.data.item : i));
      setEditingItemId(null);
    } catch (err) { showToast(err.response?.data?.message || 'Could not update.', 'error'); }
    finally { setItemEditSaving(false); }
  }

  // ── Delete catalog item ────────────────────────────────────────────────────
  async function handleDeleteItem() {
    if (!deleteItemTarget) return;
    setDeletingItem(true);
    try {
      await api.delete(`/pharmacy/items/${deleteItemTarget._id}`);
      showToast(`"${deleteItemTarget.name}" removed from catalog.`, 'success');
      setCatalogItems(prev => prev.filter(i => i._id !== deleteItemTarget._id));
      setDeleteItemTarget(null);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not remove item.', 'error');
    } finally {
      setDeletingItem(false);
    }
  }
  async function handleDelete() {
    if (!deleteTarget) return; setDeleting(true);
    try {
      const res = await api.delete(`/pharmacy/transactions/${deleteTarget._id}`);
      showToast('Transaction deleted.' + (res.data.wasAddedToBilling ? ' Note: billing amount was already updated — adjust it manually if needed.' : ''), 'success');
      setDeleteTarget(null); loadTransactions();
    } catch (err) { showToast(err.response?.data?.message || 'Could not delete.', 'error'); }
    finally { setDeleting(false); }
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const tf = f => e => setTxnForm(v => ({...v, [f]: e.target.value}));

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <AppLayout title="Pharmacy" subtitle="Manage stock, sales, and product catalog">

      {/* Low-stock notification banner */}
      {lowStockNotifs.length > 0 && (
        <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 8, background: 'var(--color-danger-soft)', border: '1px solid var(--color-danger)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: 'var(--color-danger)', marginBottom: 4 }}>Low Stock Alert</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {lowStockNotifs.map(n => (
                <span key={n.itemId} style={{ fontSize: 12, padding: '2px 10px', borderRadius: 100, background: 'var(--color-danger)', color: 'white', fontWeight: 600 }}>
                  {n.itemName}: {n.remaining} {n.unit} remaining (threshold: {n.threshold})
                </span>
              ))}
            </div>
          </div>
          <button className="btn btn-icon btn-ghost" onClick={() => setLowStockNotifs([])}>×</button>
        </div>
      )}

      {/* Summary cards */}
      {user?.isAdmin && (
        <div style={{ marginBottom:20 }}>
          <button className="btn btn-outline btn-sm" style={{ marginBottom: showStats?10:0, display:'flex', alignItems:'center', gap:6 }} onClick={toggleStats}>
            {showStats ? '▲ Hide Stats' : '▼ Show Stats'}
          </button>
          {showStats && <div className="stat-grid">
        <SC label="Stock Received (qty)" value={totals.incomingQty}       color="info"    Icon={PackageOpen} />
        <SC label="Stock Cost"           value={`₹${fmt(totals.incomingCost)}`} color="warning" Icon={IndianRupee} />
        <SC label="Items Sold (qty)"     value={totals.outgoingQty}       color="success" Icon={PackageCheck} />
        <SC label="Sales Revenue"        value={`₹${fmt(totals.outgoingRevenue)}`} color="primary" Icon={IndianRupee} />
          </div>}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display:'flex', gap:0, border:'1px solid var(--color-border)', borderRadius:10, overflow:'hidden', width:'fit-content', marginBottom:20 }}>
        {[['incoming','Incoming Stock',ArrowDown],['outgoing','Outgoing / Sales',ArrowUp],['catalog','Item Catalog',Pill]].map(([key,label,Icon]) => (
          <button key={key} type="button" onClick={() => { setActiveTab(key); setShowForm(false); }}
            style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 20px', border:'none', cursor:'pointer', background: activeTab===key ? 'var(--color-primary)' : 'transparent', color: activeTab===key ? 'white' : 'var(--color-ink-soft)', fontWeight:700, fontSize:14, transition:'all 0.15s', borderRight:'1px solid var(--color-border)' }}>
            <Icon size={15}/>{label}
          </button>
        ))}
      </div>

      {/* ── CATALOG TAB ──────────────────────────────────────────────────── */}
      {activeTab === 'catalog' && (
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <div style={{ fontSize:14, color:'var(--color-ink-soft)' }}>{catalogItems.length} items in catalog</div>
<ViewModeSwitcher mode={listMode} onChange={setListMode} />
            <button className="btn btn-primary" onClick={() => setShowItemForm(v => !v)}><Plus size={15}/>{showItemForm ? 'Cancel' : 'Add New Item'}</button>
          </div>
          {showItemForm && (
            <div className="card" style={{ padding:20, marginBottom:20 }}>
              <h3 style={{ margin:'0 0 16px', fontSize:14, fontWeight:700 }}>New Pharmacy Item</h3>
              <form onSubmit={handleCreateItem}>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:12 }}>
                  <F label="Item Name *" style={{ gridColumn:'span 2' }}><input className="input" value={itemForm.name} onChange={e => setItemForm(v=>({...v,name:e.target.value}))} placeholder="e.g. Mouthwash 250ml" required/></F>
                  <F label="Category"><select className="select" value={itemForm.category} onChange={e => setItemForm(v=>({...v,category:e.target.value}))}>{CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}</select></F>
                  <F label="Unit"><select className="select" value={itemForm.unit} onChange={e => setItemForm(v=>({...v,unit:e.target.value}))}>{UNITS.map(u=><option key={u} value={u}>{u}</option>)}</select></F>
                  <F label="Supplier Cost (₹)"><input className="input" type="number" min="0" value={itemForm.supplierCost} onChange={e => setItemForm(v=>({...v,supplierCost:e.target.value}))} placeholder="0"/></F>
                  <F label="Selling Price (₹)"><input className="input" type="number" min="0" value={itemForm.sellingPrice} onChange={e => setItemForm(v=>({...v,sellingPrice:e.target.value}))} placeholder="0"/></F>
                  <F label="Supplier"><input className="input" value={itemForm.supplier} onChange={e => setItemForm(v=>({...v,supplier:e.target.value}))} placeholder="Supplier name"/></F>
                </div>
                <F label="Description"><input className="input" value={itemForm.description} onChange={e => setItemForm(v=>({...v,description:e.target.value}))} placeholder="Specs, brand, notes…"/></F>
                <button type="submit" className="btn btn-primary btn-sm" disabled={itemSaving} style={{ marginTop:12 }}><Save size={14}/>{itemSaving ? 'Saving…' : 'Save Item'}</button>
              </form>
            </div>
          )}
          {catalogLoading ? <div className="skeleton" style={{ height:120, borderRadius:8 }}/> : null}
          {!catalogLoading && listMode === 'grid' && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:10 }}>
              {catalogItems.map((it, i) => (
                <div key={it._id} className="tilt3d-scene stagger-in" style={{ '--stagger-i': i }}>
                <div className="card tilt3d-card" style={{ padding:14, border:(it.stockQuantity||0)<=(it.lowStockThreshold??3)?'2px solid var(--color-danger)':'1px solid var(--color-border)', display:'flex', flexDirection:'column', gap:6 }}>
                  <div style={{ fontWeight:700, fontSize:13 }}>{it.name}</div>
                  <div style={{ fontSize:11, color:'var(--color-ink-faint)' }}>{it.category} · {it.unit}</div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginTop:'auto', paddingTop:6 }}>
                    <div>
                      <div style={{ fontWeight:800, fontSize:20, color:(it.stockQuantity||0)<=0?'var(--color-danger)':'var(--color-primary)' }}>{it.stockQuantity||0}</div>
                      <div style={{ fontSize:12, fontWeight:600 }}>₹{it.sellingPrice||0}</div>
                    </div>
                    <div style={{ display:'flex', gap:4, flexDirection:'column', alignItems:'flex-end' }}>
                      <button className="btn btn-outline btn-sm" style={{ padding:'2px 8px', fontSize:11 }}
                        onClick={() => { setEditingItemId(it._id); setEditItemForm({...it}); }}>Edit</button>
                      <button className="btn btn-danger btn-sm" style={{ padding:'2px 8px', fontSize:11 }}
                        onClick={() => setDeleteItemTarget(it)}>Remove</button>
                    </div>
                  </div>
                </div>
                </div>
              ))}
            </div>
          )}
          {!catalogLoading && listMode === 'compact' && (
            <div className="card" style={{ overflow:'hidden' }}>
              {catalogItems.map((it,ci) => (
                <div key={it._id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 14px', borderTop:ci===0?'none':'1px solid var(--color-border)', background:ci%2===0?'transparent':'var(--color-surface)' }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <span style={{ fontWeight:700, fontSize:13 }}>{it.name}</span>
                    <span style={{ marginLeft:8, fontSize:11, color:'var(--color-ink-faint)' }}>{it.category}</span>
                  </div>
                  <span style={{ fontSize:13, fontWeight:700, minWidth:40, textAlign:'right' }}>{it.stockQuantity||0} {it.unit}</span>
                  <span style={{ fontSize:12, color:'var(--color-primary)', fontWeight:700, minWidth:50, textAlign:'right' }}>₹{it.sellingPrice||0}</span>
                  <button className="btn btn-outline btn-sm" style={{ padding:'2px 7px', fontSize:11 }}
                    onClick={() => { setEditingItemId(it._id); setEditItemForm({...it}); }}>Edit</button>
                  <button className="btn btn-danger btn-sm" style={{ padding:'2px 7px', fontSize:11 }}
                    onClick={() => setDeleteItemTarget(it)}>Remove</button>
                </div>
              ))}
            </div>
          )}
          {!catalogLoading && listMode === 'list' && (
            <div style={{ display:'grid', gap:8 }}>
              {catalogItems.map(it => (
                <div key={it._id} className="card" style={{ padding:14, borderLeft: (it.stockQuantity||0) <= (it.lowStockThreshold??3) && (it.stockQuantity||0) >= 0 ? '3px solid var(--color-danger)' : undefined }}>
                  {editingItemId === it._id ? (
                    <div>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:10, marginBottom:10 }}>
                        <F label="Name"><input className="input" value={editItemForm.name||''} onChange={e => setEditItemForm(v=>({...v,name:e.target.value}))}/></F>
                        <F label="Category"><select className="select" value={editItemForm.category||'oral-care'} onChange={e => setEditItemForm(v=>({...v,category:e.target.value}))}>{CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}</select></F>
                        <F label="Unit"><select className="select" value={editItemForm.unit||'pcs'} onChange={e => setEditItemForm(v=>({...v,unit:e.target.value}))}>{UNITS.map(u=><option key={u} value={u}>{u}</option>)}</select></F>
                        <F label="Supplier Cost (₹)"><input className="input" type="number" value={editItemForm.supplierCost||0} onChange={e => setEditItemForm(v=>({...v,supplierCost:e.target.value}))}/></F>
                        <F label="Selling Price (₹)"><input className="input" type="number" value={editItemForm.sellingPrice||0} onChange={e => setEditItemForm(v=>({...v,sellingPrice:e.target.value}))}/></F>
                        <F label="Low Stock Alert (qty)">
                          <input className="input" type="number" min="0" value={editItemForm.lowStockThreshold??3} onChange={e => setEditItemForm(v=>({...v,lowStockThreshold:Number(e.target.value)}))}/>
                        </F>
                        <F label="Supplier"><input className="input" value={editItemForm.supplier||''} onChange={e => setEditItemForm(v=>({...v,supplier:e.target.value}))}/></F>
                      </div>
                      <F label="Description"><input className="input" value={editItemForm.description||''} onChange={e => setEditItemForm(v=>({...v,description:e.target.value}))}/></F>
                      <div style={{ display:'flex', gap:8, marginTop:10 }}>
                        <button className="btn btn-primary btn-sm" onClick={() => handleSaveItemEdit(it._id)} disabled={itemEditSaving}><Save size={13}/>{itemEditSaving?'Saving…':'Save'}</button>
                        <button className="btn btn-outline btn-sm" onClick={() => setEditingItemId(null)}>Cancel</button>
                        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, marginLeft:'auto', cursor:'pointer' }}>
                          <input type="checkbox" checked={editItemForm.isActive!==false} onChange={e => setEditItemForm(v=>({...v,isActive:e.target.checked}))} style={{ accentColor:'var(--color-primary)' }}/>
                          Active
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                      <div>
                        <div style={{ fontWeight:700, fontSize:14, display:'flex', alignItems:'center', gap:8 }}>
                          <Pill size={14} color="var(--color-primary)"/>
                          {it.name}
                          {!it.isActive && <span style={{ fontSize:11, color:'var(--color-ink-faint)' }}>(inactive)</span>}
                          {/* Stock level badge */}
                          {(it.stockQuantity||0) <= (it.lowStockThreshold??3) ? (
                            <span style={{ fontSize:11, padding:'2px 7px', borderRadius:100, background:'var(--color-danger-soft)', color:'var(--color-danger)', fontWeight:700 }}>
                              ⚠️ {it.stockQuantity||0} {it.unit} left
                            </span>
                          ) : (
                            <span style={{ fontSize:11, padding:'2px 7px', borderRadius:100, background:'var(--color-success-soft)', color:'var(--color-success)', fontWeight:600 }}>
                              {it.stockQuantity||0} {it.unit} in stock
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize:12, color:'var(--color-ink-faint)', marginTop:3, display:'flex', gap:12, flexWrap:'wrap' }}>
                          <span>{it.category} · {it.unit}</span>
                          {it.supplier && <span>Supplier: {it.supplier}</span>}
                          <span style={{ color:'var(--color-warning)' }}>Buy: ₹{fmt(it.supplierCost)}</span>
                          <span style={{ color:'var(--color-success)' }}>Sell: ₹{fmt(it.sellingPrice)}</span>
                          <span style={{ color:'var(--color-primary)' }}>Margin: ₹{fmt(it.sellingPrice - it.supplierCost)}</span>
                          <span style={{ color:'var(--color-ink-soft)' }}>Alert below: {it.lowStockThreshold??3} {it.unit}</span>
                        </div>
                        {it.description && <div style={{ fontSize:12, color:'var(--color-ink-soft)', marginTop:2, fontStyle:'italic' }}>{it.description}</div>}
                      </div>
                      <div style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-outline btn-sm" onClick={() => { setEditingItemId(it._id); setEditItemForm({...it}); }}><Pencil size={13}/> Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => setDeleteItemTarget(it)}><Trash2 size={13}/> Remove</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── INCOMING / OUTGOING TABS ──────────────────────────────────────── */}
      {activeTab !== 'catalog' && (
        <div>
          {/* Controls */}
          <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <Calendar size={15} color="var(--color-ink-faint)"/>
              <DatePicker
                selectedDate={calDate}
                selectedDates={calDates}
                onChange={d => {
                  setCalDate(d);
                  setCalDates(new Set(d==='all'?[]:[d]));
                  setDateFilter(d==='all'?'all':d);
                }}
                onMultiChange={set => {
                  setCalDates(set);
                  const arr = [...set];
                  setCalDate(arr.length===1?arr[0]:'all');
                  setDateFilter(arr.length===1?arr[0]:'all');
                }}
                availableDates={new Set()}
                multiSelect
              />

            </div>
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <Layers size={15} color="var(--color-ink-faint)"/>
              <ViewModeSwitcher mode={txnListMode} onChange={setTxnListMode} />
              <select className="select" style={{ minWidth:140, fontWeight:600 }} value={groupBy} onChange={e => { setGroupBy(e.target.value); localStorage.setItem('pharmacy_groupby', e.target.value); }}>
                <option value="none">No grouping</option>
                <option value="date">Group by Date</option>
                <option value="item">Group by Item</option>
                {activeTab === 'outgoing' && <option value="patient">Group by Patient</option>}
                {activeTab === 'incoming' && <option value="supplier">Group by Supplier</option>}
              </select>
            </div>
            <div style={{ position:'relative', flex:'1 1 200px' }}>
              <Search size={15} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--color-ink-faint)' }}/>
              <input className="input" style={{ paddingLeft:32 }} placeholder="Search item, patient, supplier…" value={search} onChange={e => setSearch(e.target.value)}/>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'var(--color-ink-soft)', whiteSpace:'nowrap' }}>
              Show <select className="select" style={{ width:72, fontWeight:600, padding:'8px 6px' }} value={limit} onChange={e => setLimit(Number(e.target.value))}>{PAGE_SIZE_OPTIONS.map(n=><option key={n} value={n}>{n}</option>)}</select> per page
            </div>
            <button className="btn btn-primary" onClick={() => setShowForm(v => !v)}><Plus size={15}/>{showForm ? 'Cancel' : (activeTab==='incoming' ? 'Add Stock' : 'Record Sale')}</button>
          </div>

          {/* Transaction form */}
          {showForm && (
            <div className="card" style={{ padding:20, marginBottom:20 }}>
              <h3 style={{ margin:'0 0 16px', fontSize:14, fontWeight:700, display:'flex', alignItems:'center', gap:8 }}>
                {activeTab==='incoming' ? <><PackageOpen size={16} color="var(--color-info)"/> Add Incoming Stock</> : <><PackageCheck size={16} color="var(--color-success)"/> Record Sale</>}
              </h3>
              <form onSubmit={handleSaveTxn}>
                {activeTab === 'incoming' ? (
                  /* ── Single-item incoming form (unchanged) ── */
                  <>
                    <F label="Item *"><ItemPicker value={selectedItem} onSelect={setSelectedItem}/></F>
                    {!selectedItem && (
                      <div style={{ fontSize:12, color:'var(--color-ink-faint)', marginBottom:12 }}>
                        Can't find the item?{' '}
                        <button type="button" style={{ border:'none', background:'none', color:'var(--color-primary)', cursor:'pointer', fontWeight:600, fontSize:12, padding:0 }}
                          onClick={() => { setActiveTab('catalog'); setShowItemForm(true); }}>
                          Add it to catalog first →
                        </button>
                      </div>
                    )}
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:12, marginTop:12, marginBottom:12 }}>
                      <F label="Quantity"><input className="input" type="number" min="1" value={txnForm.quantity} onChange={tf('quantity')}/></F>
                      <F label="Date"><input className="input" type="date" max={todayISO()} value={txnForm.date} onChange={tf('date')}/></F>
                      <F label="Supplier Cost/unit (₹)"><input className="input" type="number" min="0" value={txnForm.supplierCost} onChange={tf('supplierCost')} placeholder="0"/></F>
                      <F label="Supplier"><input className="input" value={txnForm.supplier} onChange={tf('supplier')} placeholder="Supplier name"/></F>
                    </div>
                    <F label="Notes"><input className="input" value={txnForm.notes} onChange={tf('notes')} placeholder="Reference, batch number…"/></F>
                  </>
                ) : (
                  /* ── Multi-item outgoing / sales cart ── */
                  <>
                    {/* Patient selector + editable name */}
                    <F label="Patient *"><PatientPicker value={selectedPatient} onSelect={setSelectedPatient}/></F>


                    {/* Item search to add to cart */}
                    <F label="Add Item to Cart">
                      <ItemPicker value={null} onSelect={item => { addToCart(item); }} placeholder="Search and click to add…"/>
                    </F>

                    {/* Cart table */}
                    {outgoingCart.length > 0 && (
                      <div style={{ margin:'12px 0', border:'1px solid var(--color-border)', borderRadius:8, overflow:'hidden' }}>
                        {/* Header */}
                        <div className="pharmacy-cart-header" style={{ display:'grid', gridTemplateColumns:'1fr 80px 110px 80px 32px', gap:8, padding:'7px 12px', background:'var(--color-surface)', fontSize:11, fontWeight:700, color:'var(--color-ink-faint)' }}>
                          <span>Item</span><span style={{ textAlign:'right' }}>Qty</span><span style={{ textAlign:'right' }}>Price/unit ₹</span><span style={{ textAlign:'right' }}>Total ₹</span><span/>
                        </div>
                        {outgoingCart.map(c => (
                          <div key={c.item._id} className="pharmacy-cart-row" style={{ display:'grid', gridTemplateColumns:'1fr 80px 110px 80px 32px', gap:8, padding:'8px 12px', alignItems:'center', borderTop:'1px solid var(--color-border)', fontSize:13 }}>
                            <div>
                              <div style={{ fontWeight:600 }}>{c.item.name}</div>
                              <div style={{ fontSize:11, color:'var(--color-ink-faint)' }}>{c.item.category} · {c.item.unit}</div>
                            </div>
                            <input type="number" min="1" className="input"
                              style={{ padding:'4px 6px', fontSize:13, textAlign:'right' }}
                              value={c.qty}
                              onChange={e => updateCartQty(c.item._id, e.target.value)} />
                            <input type="number" min="0" step="0.01" className="input"
                              style={{ padding:'4px 6px', fontSize:13, textAlign:'right' }}
                              value={c.sellingPrice}
                              onChange={e => updateCartPrice(c.item._id, e.target.value)} />
                            <div style={{ textAlign:'right', fontWeight:700, color:'var(--color-success)' }}>
                              ₹{fmt(c.qty * (c.sellingPrice||0))}
                            </div>
                            <button type="button" className="btn btn-icon btn-ghost" onClick={() => removeFromCart(c.item._id)}>
                              <Trash2 size={13} color="var(--color-danger)"/>
                            </button>
                          </div>
                        ))}
                        {/* Cart total */}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 80px 110px 80px 32px', gap:8, padding:'8px 12px', background:'var(--color-success-soft)', borderTop:'2px solid var(--color-success)', fontSize:13, fontWeight:800 }}>
                          <span>TOTAL</span><span/><span/>
                          <span style={{ textAlign:'right', color:'var(--color-success)' }}>₹{fmt(cartTotal)}</span><span/>
                        </div>
                      </div>
                    )}

                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:12, marginTop:12, marginBottom:12 }}>
                      <F label="Sale Date"><input className="input" type="date" max={todayISO()} value={txnForm.date} onChange={tf('date')}/></F>
                    </div>
                    <F label="Notes"><input className="input" value={txnForm.notes} onChange={tf('notes')} placeholder="Prescription reference, instructions…"/></F>
                  </>
                )}
                <div style={{ display:'flex', gap:10, marginTop:14 }}>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    <Save size={15}/>
                    {saving ? 'Saving…' : activeTab === 'outgoing' ? `Confirm Sale${outgoingCart.length > 1 ? ` (${outgoingCart.length} items)` : ''}` : 'Save'}
                  </button>
                  <button type="button" className="btn btn-outline" onClick={() => { setShowForm(false); setOutgoingCart([]); }}>Cancel</button>
                </div>
              </form>
            </div>
          )}

          {/* Transaction list — flat or grouped */}
          {loading ? <div style={{ display:'grid', gap:10 }}>{[1,2,3].map(i=><div key={i} className="skeleton" style={{ height:64, borderRadius:8 }}/>)}</div>
          : transactions.length === 0 ? (
            <div className="card" style={{ padding:48, textAlign:'center', color:'var(--color-ink-faint)' }}>
              <Pill size={36} style={{ marginBottom:12, opacity:0.4 }}/><p style={{ margin:0 }}>No records yet.</p>
            </div>
          ) : (() => {
            // Build grouped structure
            const getGroupKey = t => {
              if (groupBy === 'date')     return t.date || 'No date';
              if (groupBy === 'item')     return t.itemName || 'Unknown item';
              if (groupBy === 'patient')  return t.patientName ? `${t.patientName}${t.patientUID ? ` (${t.patientUID})` : ''}` : 'No patient';
              if (groupBy === 'supplier') return t.supplier || 'No supplier';
              return '__all__';
            };
            const groups = {};
            transactions.forEach(t => {
              const k = getGroupKey(t);
              if (!groups[k]) groups[k] = [];
              groups[k].push(t);
            });
            const groupEntries = Object.entries(groups);

            return (
              <div style={{ display:'grid', gap: groupBy === 'none' ? 8 : 16 }}>
                {groupEntries.map(([groupKey, groupTxns]) => (
                  <div key={groupKey} className={groupBy !== 'none' ? 'card' : undefined} style={groupBy !== 'none' ? { overflow:'hidden' } : undefined}>
                    {/* Group header */}
                    {groupBy !== 'none' && (
                      groupBy === 'patient' ? (
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10, padding:'10px 14px', background:'var(--color-surface)', borderBottom:'1px solid var(--color-border)' }}>
                          <div>
                            <div style={{ fontWeight:800, fontSize:15 }}>
                              {groupTxns[0]?.patientName || 'No patient'}
                              {groupTxns[0]?.patientUID && <span style={{ marginLeft:10, fontSize:11, fontWeight:400, color:'var(--color-ink-faint)', fontFamily:'var(--font-mono)' }}>{groupTxns[0].patientUID}</span>}
                            </div>
                            <div style={{ fontSize:12, color:'var(--color-ink-faint)', marginTop:2 }}>
                              {groupTxns.length} item{groupTxns.length !== 1 ? 's' : ''}
                            </div>
                          </div>
                          <div style={{ textAlign:'right' }}>
                            <div style={{ fontSize:10, color:'var(--color-ink-faint)', fontWeight:700 }}>TOTAL</div>
                            <div style={{ fontWeight:800, fontSize:16, color:'var(--color-primary)' }}>
                              ₹{fmt(groupTxns.reduce((s,t) => s + (t.quantity||0) * (t.sellingPrice||0), 0))}
                            </div>
                          </div>
                        </div>
                      ) : (
                      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'var(--color-surface)', borderBottom:'1px solid var(--color-border)' }}>
                        <Layers size={13} color="var(--color-ink-faint)"/>
                        <span style={{ fontWeight:700, fontSize:13, color:'var(--color-ink)' }}>{groupKey}</span>
                        <span style={{ fontSize:12, color:'var(--color-ink-faint)' }}>· {groupTxns.length} record{groupTxns.length !== 1 ? 's' : ''}</span>
                        {groupBy === 'item' && (
                          <span style={{ marginLeft:'auto', fontSize:12, fontWeight:700, color: activeTab === 'incoming' ? 'var(--color-info)' : 'var(--color-success)' }}>
                            Total qty: {groupTxns.reduce((s,t) => s + (t.quantity||0), 0)}&nbsp;
                            · ₹{fmt(groupTxns.reduce((s,t) => s + (t.quantity||0) * (activeTab==='incoming' ? (t.supplierCost||0) : (t.sellingPrice||0)), 0))}
                          </span>
                        )}
                        {groupBy === 'date' && (
                          <span style={{ marginLeft:'auto', fontSize:12, fontWeight:700, color:'var(--color-ink-soft)' }}>
                            Total: ₹{fmt(groupTxns.reduce((s,t) => s + (t.quantity||0) * (activeTab==='incoming' ? (t.supplierCost||0) : (t.sellingPrice||0)), 0))}
                          </span>
                        )}
                      </div>
                      )
                    )}
                    {/* Transaction cards */}
                    {txnListMode === 'compact' ? (
                      <div className={groupBy === 'none' ? 'card' : undefined} style={{ overflow:'hidden' }}>
                        {groupTxns.map((t,ci) => (
                          <div key={t._id} style={{ padding: editingId===t._id ? '10px 14px' : '7px 14px', borderTop:ci===0?'none':'1px solid var(--color-border)', background:ci%2===0?'transparent':'var(--color-surface)' }}>
                            {editingId === t._id ? (
                              <TxnEditForm t={t} editForm={editForm} setEditForm={setEditForm} setEditingId={setEditingId} setTransactions={setTransactions} showToast={showToast} />
                            ) : (
                              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                <div style={{ flex:1, minWidth:0 }}>
                                  <span style={{ fontWeight:700, fontSize:13 }}>{t.itemName}</span>
                                  {t.patientName && <span style={{ marginLeft:8, fontSize:11, color:'var(--color-ink-faint)' }}>{t.patientName}</span>}
                                  {t.labName && <span style={{ marginLeft:8, fontSize:11, color:'var(--color-ink-faint)' }}>{t.labName}</span>}
                                </div>
                                <span style={{ fontSize:11, color:'var(--color-ink-faint)' }}>{t.date?.slice(0,10)}</span>
                                <span style={{ fontSize:12, fontWeight:700, color:'var(--color-ink)', minWidth:32, textAlign:'center' }}>×{t.quantity}</span>
                                <span style={{ fontSize:12, fontWeight:700, color: t.transactionType==='incoming'?'var(--color-info)':'var(--color-success)', minWidth:60, textAlign:'right' }}>
                                  ₹{fmt((t.quantity||0) * (t.transactionType==='incoming' ? (t.supplierCost||0) : (t.sellingPrice||0)))}
                                </span>
                                <button className="btn btn-outline btn-sm" style={{ padding:'3px 8px', fontSize:11 }} onClick={() => { setEditingId(t._id); setEditForm({...t}); }}><Pencil size={12}/></button>
                                <button className="btn btn-danger btn-sm" style={{ padding:'3px 8px', fontSize:11 }} onClick={() => setDeleteTarget(t)}><Trash2 size={12}/></button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : txnListMode === 'grid' ? (
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:10, padding: groupBy !== 'none' ? 12 : 0 }}>
                        {groupTxns.map((t, i) => (
                          <div key={t._id} className="tilt3d-scene stagger-in" style={{ '--stagger-i': i }}>
                          <div className="tilt3d-card" style={{ padding:12, border:'1px solid var(--color-border)', borderRadius:8, display:'flex', flexDirection:'column', gap:6 }}>
                            {editingId === t._id ? (
                              <TxnEditForm t={t} editForm={editForm} setEditForm={setEditForm} setEditingId={setEditingId} setTransactions={setTransactions} showToast={showToast} />
                            ) : (
                              <>
                                <div style={{ fontWeight:700, fontSize:13 }}>{t.itemName}</div>
                                <div style={{ fontSize:11, color:'var(--color-ink-faint)' }}>
                                  {t.patientName || t.labName || t.supplier || '—'}
                                </div>
                                <div style={{ fontSize:11, color:'var(--color-ink-faint)' }}>{t.date?.slice(0,10)}</div>
                                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'auto', paddingTop:6 }}>
                                  <span style={{ fontSize:12, fontWeight:700 }}>×{t.quantity}</span>
                                  <span style={{ fontWeight:800, fontSize:14, color: t.transactionType==='incoming'?'var(--color-info)':'var(--color-success)' }}>
                                    ₹{fmt((t.quantity||0) * (t.transactionType==='incoming' ? (t.supplierCost||0) : (t.sellingPrice||0)))}
                                  </span>
                                </div>
                                <div style={{ display:'flex', gap:4, paddingTop:4 }}>
                                  <button className="btn btn-outline btn-sm" style={{ flex:1, fontSize:11, padding:'3px 0' }} onClick={() => { setEditingId(t._id); setEditForm({...t}); }}>Edit</button>
                                  <button className="btn btn-sm" style={{ flex:1, fontSize:11, padding:'3px 0', background:'var(--color-danger-soft)', color:'var(--color-danger)', border:'none' }} onClick={() => setDeleteTarget(t)}>Del</button>
                                </div>
                              </>
                            )}
                          </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                    <div>
                      {groupTxns.map((t,ti) => (
                        <div key={t._id} className={groupBy === 'none' ? 'card' : undefined} style={{ padding:14, borderTop: groupBy !== 'none' && ti>0 ? '1px solid var(--color-border)' : 'none' }}>
                          {editingId === t._id ? (
                            <div>
                              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))', gap:10, marginBottom:10 }}>
                                <F label="Qty"><input className="input" type="number" value={editForm.quantity||1} onChange={e => setEditForm(v=>({...v,quantity:e.target.value}))}/></F>
                                <F label="Date"><input className="input" type="date" max={todayISO()} value={editForm.date||''} onChange={e => setEditForm(v=>({...v,date:e.target.value}))}/></F>
                                {t.transactionType==='incoming' && <F label="Supplier Cost"><input className="input" type="number" value={editForm.supplierCost||0} onChange={e => setEditForm(v=>({...v,supplierCost:e.target.value}))}/></F>}
                                {t.transactionType==='outgoing' && <F label="Selling Price"><input className="input" type="number" value={editForm.sellingPrice||0} onChange={e => setEditForm(v=>({...v,sellingPrice:e.target.value}))}/></F>}
                              </div>
                              <F label="Notes"><input className="input" value={editForm.notes||''} onChange={e => setEditForm(v=>({...v,notes:e.target.value}))}/></F>
                              <div style={{ display:'flex', gap:8, marginTop:10 }}>
                                <button className="btn btn-primary btn-sm" onClick={async () => {
                                  try { const r = await api.put(`/pharmacy/transactions/${t._id}`, editForm); setTransactions(prev => prev.map(x => x._id===t._id ? r.data.transaction : x)); setEditingId(null); showToast('Updated.','success'); }
                                  catch(err) { showToast(err.response?.data?.message||'Error','error'); }
                                }}><Save size={13}/>Save</button>
                                <button className="btn btn-outline btn-sm" onClick={() => setEditingId(null)}>Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>
                              <div>
                                <div style={{ fontWeight:700, fontSize:14, display:'flex', alignItems:'center', gap:8 }}>
                                  {t.transactionType==='incoming' ? <ArrowDown size={14} color="var(--color-info)"/> : <ArrowUp size={14} color="var(--color-success)"/>}
                                  {t.itemName}
                                  <span style={{ fontWeight:400, fontSize:13, color:'var(--color-ink-soft)' }}>{t.quantity} {t.unit}</span>
                                </div>
                                <div style={{ fontSize:12, color:'var(--color-ink-faint)', marginTop:3, display:'flex', gap:12, flexWrap:'wrap' }}>
                                  <span>{t.date}</span>
                                  {t.transactionType==='incoming' && <>
                                    {t.supplier && <span>From: {t.supplier}</span>}
                                    <span style={{ color:'var(--color-warning)' }}>Cost: ₹{fmt(t.supplierCost)}/unit · Total: ₹{fmt(t.quantity * t.supplierCost)}</span>
                                  </>}
                                  {t.transactionType==='outgoing' && <>
                                    {t.patientName && <span>Patient: <strong>{t.patientName}</strong> ({t.patientUID})</span>}
                                    <span style={{ color:'var(--color-success)' }}>Sell: ₹{fmt(t.sellingPrice)}/unit · Total: ₹{fmt(t.quantity * t.sellingPrice)}</span>
                                  </>}
                                  {t.notes && <span style={{ fontStyle:'italic' }}>{t.notes}</span>}
                                </div>
                              </div>
                              <div style={{ display:'flex', gap:6 }}>
                                <button className="btn btn-outline btn-sm" onClick={() => { setEditingId(t._id); setEditForm({...t}); }}><Pencil size={13}/></button>
                                <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(t)}><Trash2 size={13}/></button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Pagination */}
          {!loading && total > 0 && (
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:20, flexWrap:'wrap', gap:10 }}>
              <div style={{ fontSize:13, color:'var(--color-ink-soft)' }}>Showing <strong>{((page-1)*limit)+1}</strong>–<strong>{Math.min(page*limit,total)}</strong> of <strong>{total}</strong></div>
              <div style={{ display:'flex', gap:4 }}>
                <button className="btn btn-outline btn-sm" disabled={page===1} onClick={() => setPage(1)}>«</button>
                <button className="btn btn-outline btn-sm" disabled={page===1} onClick={() => setPage(p=>p-1)}>‹</button>
                <span style={{ padding:'0 12px', fontSize:13, fontWeight:600, display:'flex', alignItems:'center' }}>Page {page} of {totalPages}</span>
                <button className="btn btn-outline btn-sm" disabled={page===totalPages} onClick={() => setPage(p=>p+1)}>›</button>
                <button className="btn btn-outline btn-sm" disabled={page===totalPages} onClick={() => setPage(totalPages)}>»</button>
              </div>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title={`Delete transaction for "${deleteTarget?.itemName}"?`}
        message="This will permanently remove the transaction record."
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        danger onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={!!deleteItemTarget}
        title={`Remove "${deleteItemTarget?.name}" from catalog?`}
        message="This removes it from the item list. Past transactions that used this item are not affected — they store the item name directly."
        confirmLabel={deletingItem ? 'Removing…' : 'Remove Item'}
        danger onConfirm={handleDeleteItem} onCancel={() => setDeleteItemTarget(null)}
      />
      <style>{`@keyframes spin { from{transform:translateY(-50%) rotate(0deg)} to{transform:translateY(-50%) rotate(360deg)} }`}</style>
    </AppLayout>
  );
}

// Inline edit form for a pharmacy transaction — used in compact and grid views
function TxnEditForm({ t, editForm, setEditForm, setEditingId, setTransactions, showToast }) {
  async function save() {
    try {
      const r = await api.put(`/pharmacy/transactions/${t._id}`, editForm);
      setTransactions(prev => prev.map(x => x._id===t._id ? r.data.transaction : x));
      setEditingId(null);
      showToast('Updated.', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Error', 'error');
    }
  }
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(100px,1fr))', gap:8, marginBottom:8 }}>
        <F label="Qty"><input className="input" type="number" value={editForm.quantity||1} onChange={e => setEditForm(v=>({...v,quantity:e.target.value}))}/></F>
        <F label="Date"><input className="input" type="date" max={todayISO()} value={editForm.date||''} onChange={e => setEditForm(v=>({...v,date:e.target.value}))}/></F>
        {t.transactionType==='incoming' && <F label="Cost"><input className="input" type="number" value={editForm.supplierCost||0} onChange={e => setEditForm(v=>({...v,supplierCost:e.target.value}))}/></F>}
        {t.transactionType==='outgoing' && <F label="Price"><input className="input" type="number" value={editForm.sellingPrice||0} onChange={e => setEditForm(v=>({...v,sellingPrice:e.target.value}))}/></F>}
      </div>
      <F label="Notes"><input className="input" value={editForm.notes||''} onChange={e => setEditForm(v=>({...v,notes:e.target.value}))}/></F>
      <div style={{ display:'flex', gap:6, marginTop:8 }}>
        <button className="btn btn-primary btn-sm" style={{ flex:1, fontSize:11, padding:'3px 0' }} onClick={save}>Save</button>
        <button className="btn btn-outline btn-sm" style={{ flex:1, fontSize:11, padding:'3px 0' }} onClick={() => setEditingId(null)}>Cancel</button>
      </div>
    </div>
  );
}

function SC({ label, value, color, Icon }) {
  return (
    <div className="stat-card">
      <div className="stat-card-icon" style={{ background:`var(--color-${color}-soft)` }}><Icon size={18} color={`var(--color-${color})`}/></div>
      <div className="stat-card-label">{label}</div>
      <div className="stat-card-value">{value}</div>
    </div>
  );
}
function F({ label, children, style }) {
  return <div className="field" style={{ marginBottom:0, ...style }}><label className="label">{label}</label>{children}</div>;
}
